import axios from 'axios';
import type { AxiosInstance, AxiosResponse } from 'axios';

import { env } from 'env';
import { errorInterceptors } from 'api/interceptors';

import { getOktaBearerToken } from 'helpers/okta-session';

const BASE_URL = env.REACT_APP_API_URL
  ? `${env.REACT_APP_API_URL}/api`
  : '/api'; // package.json proxies this appropriately when running locally, so we can just use the relative path)

const API_HEALTH_URL = env.REACT_APP_API_URL
  ? `${env.REACT_APP_API_URL}/management/health`
  : '/management/health';

const API_INFO_URL = `${env.REACT_APP_API_URL}/management/info`;

axios.defaults.headers.post['Content-Type'] = 'application/json';
axios.defaults.headers.put['Content-Type'] = 'application/json';
axios.defaults.headers.patch['Content-Type'] = 'application/json';

export const getApiHealth = (): Promise<AxiosResponse | unknown> =>
  axios
    .get(API_HEALTH_URL)
    .then((resp) => resp)
    .catch((err) => err);

export const getApiInfo = (): Promise<AxiosResponse | unknown> =>
  axios
    .get(API_INFO_URL)
    .then((resp) => resp)
    .catch((err) => err);

export interface MarketViewApiInstance extends AxiosInstance {
  textpost: <T = unknown>(
    path: string,
    data: unknown
  ) => Promise<AxiosResponse<T>>;
}

const api = axios.create({
  baseURL: BASE_URL,
  paramsSerializer: { indexes: null },
}) as MarketViewApiInstance;

api.interceptors.request.use((config) => {
  const token = getOktaBearerToken();

  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }

  return config;
});

api.interceptors.response.use((response) => response, errorInterceptors);

api.textpost = <T = unknown>(path: string, data: unknown) =>
  api.post<T>(path, data, {
    headers: {
      'Content-Type': 'text/plain',
    },
  });

export interface StreamFetchChunk {
  content?: string;
  [key: string]: unknown;
}

export interface StreamFetchOptions {
  body?: unknown;
  method?: string;
  headers?: Record<string, string>;
  onChunk?: (parsed: unknown, fullContent: string) => void;
  onError?: (error: Error) => void;
  onComplete?: (fullContent: string, chunks: unknown[]) => void;
  parseJson?: boolean;
  signal?: AbortSignal;
}

export interface StreamFetchResult {
  content: string;
  chunks: unknown[];
  aborted?: boolean;
}

interface ProblemDetail {
  type?: string;
  title?: string;
  detail?: string;
  [key: string]: unknown;
}

const isProblemDetail = (value: unknown): value is ProblemDetail => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return false;
  }

  const record = value as Record<string, unknown>;

  return (
    (record.type === undefined || typeof record.type === 'string') &&
    (record.detail === undefined || typeof record.detail === 'string')
  );
};

export class StreamFetchError extends Error {
  status: number;
  statusText?: string;
  data?: ProblemDetail;

  constructor(
    status: number,
    { statusText, data }: { statusText?: string; data?: ProblemDetail } = {}
  ) {
    super(data?.detail ?? `HTTP error! status: ${status}`);
    this.name = 'StreamFetchError';
    this.status = status;
    this.statusText = statusText;
    this.data = data;
  }
}

/**
 * Universal SSE streaming fetch.
 */
export const streamFetch = async (
  url: string,
  options: StreamFetchOptions = {}
): Promise<StreamFetchResult> => {
  const {
    body,
    method = 'POST',
    headers = {},
    onChunk,
    onError,
    onComplete,
    parseJson = true,
    signal,
  } = options;

  const defaultHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${getOktaBearerToken() ?? ''}`,
    Accept: 'text/event-stream',
  };

  const response = await fetch(`${BASE_URL}${url}`, {
    method,
    headers: { ...defaultHeaders, ...headers },
    body: body ? JSON.stringify(body) : undefined,
    signal,
  });

  if (!response.ok) {
    let data: ProblemDetail | undefined;

    try {
      const parsed: unknown = await response.json();

      if (isProblemDetail(parsed)) {
        data = parsed;
      }
    } catch {
      // Response body was empty or not JSON; fall back to status-only error.
    }

    const error = new StreamFetchError(response.status, {
      statusText: response.statusText,
      data,
    });

    onError?.(error);
    throw error;
  }

  if (!response.body) {
    const error = new Error('Response body is empty');

    onError?.(error);
    throw error;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();

  let fullContent = '';
  let buffer = '';
  const chunks: unknown[] = [];

  const processLine = (line: string): void => {
    if (!line.trim()) return;

    if (line.startsWith('data:')) {
      const data = line.slice(5).trim();

      if (data === '[DONE]') return;

      let parsed: unknown = data;

      if (parseJson && data) {
        try {
          parsed = JSON.parse(data);
        } catch {
          // Keep as string if not valid JSON
        }
      }

      const content =
        typeof parsed === 'string'
          ? parsed
          : (parsed as StreamFetchChunk)?.content ?? '';

      fullContent += content;
      chunks.push(parsed);
      onChunk?.(parsed, fullContent);
    }
  };

  try {
    while (true) {
      const { done, value } = await reader.read();

      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');

      buffer = lines.pop() ?? '';

      for (const line of lines) {
        processLine(line);
      }
    }

    if (buffer.trim()) {
      processLine(buffer);
    }
  } catch (error) {
    const err = error as Error;

    if (err.name === 'AbortError') {
      onError?.(new Error('Stream aborted'));

      return { content: fullContent, chunks, aborted: true };
    }

    onError?.(err);
    throw err;
  } finally {
    reader.releaseLock();
  }

  onComplete?.(fullContent, chunks);

  return { content: fullContent, chunks };
};

export default api;
